import useToggleDriveMode from "@/components/AppHeader/hooks/useToggleDriveMode";
import {
  CursorType,
  setIsSpacePressed,
  setIsMiddleMousePressed,
  setIsMetaPressed,
  setIsCtrlPressed,
  setTriggerDeleteElement,
} from "@/store/globalSettingsSlice";
import type { RootState } from "@/store/store";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { useCallback, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { TempoElement } from "@/utils/tempoElement";
import { useActiveCanvas, useAutoUpdateRef } from "@/utils/utils";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { getUpdateClassChangeForElement } from "@/components/RightStyle/TailwindStylingPanel/utils/updateElementUtils";
import { UpdateStoryboardsChange } from "@/store/zustand/changeLedgerTypes";
import { useExtractElementStore } from "@/components/Canvas/stores/extractElementStore";
import { useIframeStore } from "@/components/Canvas/stores/iframeStore";
import { useElementReorderShortcuts } from "@/hooks/useElementReorderShortcuts";
import { useUserPreferences } from "@/components/UserPreferences/useUserPreferences";
import {
  PropertyType,
  checkArbitraryTailwindClass,
} from "@/tailwind/tailwindUtils";
import { useTypographyShortcuts } from "./useTypographyAdjustment";
import { usePagesStore } from "@/store/zustand/pagesStore";
import { useCanvasId } from "@/utils/utils";
import { parseNumericValue } from "@/utils/typographyAdjustments";
import _ from "lodash";

const ARROW_KEY_TO_DIRECTION: Record<string, "up" | "down" | "left" | "right"> =
  {
    ArrowUp: "up",
    ArrowDown: "down",
    ArrowLeft: "left",
    ArrowRight: "right",
  };

const extractPositionFromClasses = (
  elementClasses: string[],
  propertyType: PropertyType.LEFT | PropertyType.TOP,
): number | null => {
  const tailwindClassesMap = useCodebaseStore.getState().tailwindClassesMap;

  for (const className of elementClasses) {
    const arbitraryMatch = checkArbitraryTailwindClass(className, propertyType);
    if (arbitraryMatch?.propertyValue) {
      const numericValue = parseNumericValue(arbitraryMatch.propertyValue);
      if (numericValue !== null) return numericValue;
    }

    const matchedClass = tailwindClassesMap[className];
    if (matchedClass?.propertyType === propertyType) {
      const numericValue = parseNumericValue(matchedClass.css);
      if (numericValue !== null) return numericValue;
    }
  }

  return null;
};

export const useOnKeyDown = (
  iframesRef?: React.RefObject<
    Record<string, { ref: any; width: number; height: number }>
  >,
) => {
  const { toggleCursor } = useToggleDriveMode({
    fromKey: true,
  });

  const dispatch = useDispatch();
  const undo = useDesignCommandManager((state) => state.undo);
  const redo = useDesignCommandManager((state) => state.redo);
  const selectedElementKey = useSelector(
    (state: any) => state.iframe.selectedElementKey,
  );
  const multiSelectedElementKeys = useSelector(
    (state: any) => state.iframe.multiSelectedElementKeys,
  );
  const selectedElementBoundingBox = useExtractElementStore(
    (state) => state.selectedElementBoundingBox,
  );
  const dragFinishedTrigger = useIframeStore(
    (state) => state.dragFinishedTrigger,
  );
  const { reorderElement, canReorderInDirection } =
    useElementReorderShortcuts();
  const { smallNudge, largeNudge } = useUserPreferences();

  const canvasId = useCanvasId();
  const selectedPageId = usePagesStore(
    (state) => state.selectedPageId[canvasId],
  );
  const getPageSourcePath = usePagesStore((state) => state.getPageSourcePath);

  const activeCanvas = useActiveCanvas();
  const accumulatedNudgeRef = useRef({ dx: 0, dy: 0 });
  const basePositionsRef = useRef<Map<string, { x: number; y: number }>>(
    new Map(),
  );
  const nudgeActiveRef = useRef(false);
  const isMountedRef = useRef(true);
  const selectedElementKeyRef = useAutoUpdateRef(selectedElementKey);
  const multiSelectedElementKeysRef = useAutoUpdateRef(
    multiSelectedElementKeys,
  );
  const activeCanvasRef = useAutoUpdateRef(activeCanvas);
  const selectedPageIdRef = useAutoUpdateRef(selectedPageId);
  const canvasIdRef = useAutoUpdateRef(canvasId);

  // Reset nudge state when selection changes
  useEffect(() => {
    if (!nudgeActiveRef.current) {
      basePositionsRef.current.clear();
      accumulatedNudgeRef.current = { dx: 0, dy: 0 };
    }
  }, [selectedElementKey, multiSelectedElementKeys]);

  // Cancel any pending nudge operations when drag completes to prevent race conditions
  useEffect(() => {
    debouncedExecuteNudgeCommand.current.cancel();
    basePositionsRef.current.clear();
    accumulatedNudgeRef.current = { dx: 0, dy: 0 };
    nudgeActiveRef.current = false;
  }, [dragFinishedTrigger]);

  const applyNudgeChanges = useCallback(
    (dx: number, dy: number, isInstant: boolean) => {
      if (basePositionsRef.current.size === 0) return;

      const allSelectedKeys = [
        ...(selectedElementKeyRef.current
          ? [selectedElementKeyRef.current]
          : []),
        ...(multiSelectedElementKeysRef.current || []),
      ];
      const uniqueKeys = Array.from(new Set(allSelectedKeys));
      const storyboardDeltas: any[] = [];

      for (const key of uniqueKeys) {
        const element = TempoElement.fromKey(key);
        if (element.isEmpty()) continue;

        const basePosition = basePositionsRef.current.get(key);
        if (!basePosition) continue;

        if (element.isStoryboard()) {
          const sb = activeCanvasRef.current?.storyboards.find(
            (s) => s.id === element.storyboardId,
          );
          if (sb && selectedPageIdRef.current) {
            const pageConfigPath = getPageSourcePath(
              canvasIdRef.current,
              selectedPageIdRef.current,
            );
            storyboardDeltas.push({
              changeType: "upsert" as const,
              storyboardId: element.storyboardId,
              pageId: selectedPageIdRef.current,
              pageConfigPath,
              storyboardUpdateDict: {
                x: basePosition.x + dx,
                y: basePosition.y + dy,
              },
            });
          }
          continue;
        }

        if (element.isKnownElement()) {
          const change = getUpdateClassChangeForElement({
            codebaseId: element.codebaseId,
            newLeft: Math.round(basePosition.x + dx),
            newTop: Math.round(basePosition.y + dy),
          });
          if (change) {
            const manager = useDesignCommandManager.getState();
            isInstant
              ? manager.sendInstantChange(change)
              : manager.executeCommand(change);
          }
        }
      }

      if (storyboardDeltas.length && activeCanvasRef.current?.id) {
        const change = new UpdateStoryboardsChange({
          canvasId: activeCanvasRef.current.id,
          deltas: storyboardDeltas,
        });
        const manager = useDesignCommandManager.getState();
        isInstant
          ? manager.sendInstantChange(change)
          : manager.executeCommand(change);
      }
    },
    [selectedElementKeyRef, multiSelectedElementKeysRef],
  );

  const applyNudgeChangesRef = useAutoUpdateRef(applyNudgeChanges);

  // Typography shortcuts hook
  const { adjustTypography, cleanup } = useTypographyShortcuts({
    iframesRef,
    selectedElementKeyRef,
    multiSelectedElementKeysRef,
    isMountedRef,
  });

  const debouncedExecuteNudgeCommand = useRef(
    _.debounce(() => {
      if (!isMountedRef.current || basePositionsRef.current.size === 0) return;

      const finalDx = accumulatedNudgeRef.current.dx;
      const finalDy = accumulatedNudgeRef.current.dy;

      if (finalDx === 0 && finalDy === 0) return;

      try {
        applyNudgeChangesRef.current(finalDx, finalDy, false);
      } catch (error) {
        console.warn("Nudge command execution failed:", error);
      } finally {
        // Always reset state regardless of success/failure
        accumulatedNudgeRef.current = { dx: 0, dy: 0 };
        basePositionsRef.current.clear();
        nudgeActiveRef.current = false;
      }
    }, 500),
  );

  const captureStoryboardPosition = useCallback(
    (element: TempoElement, key: string) => {
      const sb = activeCanvas?.storyboards.find(
        (s) => s.id === element.storyboardId,
      );
      if (sb) {
        basePositionsRef.current.set(key, { x: sb.x, y: sb.y });
      }
    },
    [activeCanvas],
  );

  const captureElementPosition = useCallback(
    (element: TempoElement, key: string) => {
      const treeElement =
        useCodebaseStore.getState().treeElementLookup[element.codebaseId];
      if (!treeElement) return;

      const classes: string[] = treeElement.removableClasses || [];
      const hasAbsolute =
        classes.includes("absolute") || classes.includes("fixed");
      if (!hasAbsolute) return;

      let left = extractPositionFromClasses(classes, PropertyType.LEFT);
      let top = extractPositionFromClasses(classes, PropertyType.TOP);

      // Fallback to bounding box for selected element
      if ((left === null || top === null) && key === selectedElementKey) {
        if (selectedElementBoundingBox) {
          left =
            left ??
            selectedElementBoundingBox.relativeX ??
            selectedElementBoundingBox.x ??
            0;
          top =
            top ??
            selectedElementBoundingBox.relativeY ??
            selectedElementBoundingBox.y ??
            0;
        }
      }

      if (left !== null && top !== null) {
        basePositionsRef.current.set(key, { x: left, y: top });
      }
    },
    [selectedElementKey, selectedElementBoundingBox],
  );

  const nudgeSelectedElements = useCallback(
    (dx: number, dy: number) => {
      try {
        const allSelectedKeys = [
          ...(selectedElementKey ? [selectedElementKey] : []),
          ...(multiSelectedElementKeys || []),
        ];
        const uniqueKeys = Array.from(new Set(allSelectedKeys));
        if (!uniqueKeys.length) return false;

        // Capture base positions on first nudge
        if (basePositionsRef.current.size === 0) {
          nudgeActiveRef.current = true;

          for (const key of uniqueKeys) {
            const element = TempoElement.fromKey(key);
            if (element.isEmpty()) continue;

            if (element.isStoryboard()) {
              captureStoryboardPosition(element, key);
            } else if (element.isKnownElement()) {
              captureElementPosition(element, key);
            }
          }
        }

        // Accumulate nudges
        accumulatedNudgeRef.current.dx += dx;
        accumulatedNudgeRef.current.dy += dy;

        // Apply instant visual feedback
        applyNudgeChanges(
          accumulatedNudgeRef.current.dx,
          accumulatedNudgeRef.current.dy,
          true,
        );

        debouncedExecuteNudgeCommand.current();

        return true;
      } catch (error) {
        console.warn("Nudge operation failed:", error);
        nudgeActiveRef.current = false;
        return false;
      }
    },
    [
      selectedElementKey,
      multiSelectedElementKeys,
      applyNudgeChanges,
      captureStoryboardPosition,
      captureElementPosition,
    ],
  );

  // Handler for typography shortcuts from useKeyboardShortcuts
  const handleTypographyAdjustment = useCallback(
    (propertyType: string, delta: number) => {
      const propertyTypeMap: Record<string, PropertyType> = {
        fontSize: PropertyType.FONT_SIZE,
        fontWeight: PropertyType.FONT_WEIGHT,
        letterSpacing: PropertyType.LETTER_SPACING,
        lineHeight: PropertyType.LINE_HEIGHT,
      };

      const actualPropertyType = propertyTypeMap[propertyType];
      if (!actualPropertyType) return;

      adjustTypography(actualPropertyType, delta);
    },
    [adjustTypography],
  );

  const onKeyDown = useCallback(
    (e) => {
      const arrowKeys = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];
      // Allow repeats only for arrow keys.
      // For other keys, block repeated Meta/Ctrl combos (e.g., Ctrl+V) to avoid spamming.
      if (e.repeat && !arrowKeys.includes(e.key)) {
        if (e.metaKey || e.ctrlKey) {
          e?.preventDefault?.();
          e?.stopPropagation?.();
        }
        return;
      }

      const activeElement = e.activeElement || document.activeElement;
      const isEditableElement =
        activeElement.tagName === "INPUT" ||
        activeElement.tagName === "TEXTAREA" ||
        activeElement.isContentEditable;

      if (isEditableElement) {
        return;
      }

      if (e.code === "Space") {
        dispatch(setIsSpacePressed(true));
      }

      if (e.key === "Meta") {
        dispatch(setIsMetaPressed(true));
      }

      if (e.key === "Control") {
        dispatch(setIsCtrlPressed(true));
      }

      if (e.key === "z" && (e.metaKey || e.ctrlKey)) {
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }

        e?.preventDefault?.();
        e?.stopPropagation?.();
      }

      if (e.key === "backspace" || e.key === "Delete") {
        dispatch(setTriggerDeleteElement(true));
      }

      if (arrowKeys.includes(e.key)) {
        const direction = ARROW_KEY_TO_DIRECTION[e.key];

        if (direction && !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey) {
          if (canReorderInDirection(direction)) {
            e?.preventDefault?.();
            e?.stopPropagation?.();
            reorderElement(direction);
            return;
          }
        }

        const delta = e.shiftKey ? largeNudge : smallNudge;
        const dx =
          e.key === "ArrowLeft" ? -delta : e.key === "ArrowRight" ? delta : 0;
        const dy =
          e.key === "ArrowUp" ? -delta : e.key === "ArrowDown" ? delta : 0;

        if (nudgeSelectedElements(dx, dy)) {
          e?.preventDefault?.();
          e?.stopPropagation?.();
          return;
        }
      }
    },
    [
      dispatch,
      redo,
      undo,
      nudgeSelectedElements,
      canReorderInDirection,
      reorderElement,
      smallNudge,
      largeNudge,
    ],
  );

  useEffect(() => {
    isMountedRef.current = true;
    const debounced = debouncedExecuteNudgeCommand.current;
    return () => {
      isMountedRef.current = false;
      debounced.cancel();
      cleanup();
    };
  }, [cleanup]);

  return { onKeyDown, handleTypographyAdjustment };
};

export const useOnKeyUp = () => {
  const { toggleCursor } = useToggleDriveMode({
    fromKey: true,
  });

  const dispatch = useDispatch();

  const cursorType = useSelector(
    (state: RootState) => state.globalSettings.cursorType,
  );
  const cursorTypeFromKeyDown = useSelector(
    (state: RootState) => state.globalSettings.cursorTypeFromKeyDown,
  );

  const cursorTypeRef = useRef(cursorType);
  const cursorTypeFromKeyDownRef = useRef(cursorTypeFromKeyDown);
  const toggleCursorRef = useRef(toggleCursor);

  useEffect(() => {
    cursorTypeRef.current = cursorType;
  }, [cursorType]);

  useEffect(() => {
    cursorTypeFromKeyDownRef.current = cursorTypeFromKeyDown;
  }, [cursorTypeFromKeyDown]);

  useEffect(() => {
    toggleCursorRef.current = toggleCursor;
  }, [toggleCursor]);

  const onKeyUp = useCallback(
    (e) => {
      if (e.repeat) return;

      const activeElement = e.activeElement || document.activeElement;
      const isEditableElement =
        activeElement.tagName === "INPUT" ||
        activeElement.tagName === "TEXTAREA" ||
        activeElement.isContentEditable;

      if (isEditableElement) {
        return;
      }

      if (e.code === "Space") {
        dispatch(setIsSpacePressed(false));
      }

      if (e.key === "Meta") {
        dispatch(setIsMetaPressed(false));
      }

      if (e.key === "Control") {
        dispatch(setIsCtrlPressed(false));
      }

      if (e.key === "Escape" && cursorTypeRef.current === "drive") {
        toggleCursorRef.current();
      }
    },
    [dispatch],
  );

  return onKeyUp;
};

export const useOnMouseDown = () => {
  const dispatch = useDispatch();

  return useCallback(
    (e: MouseEvent) => {
      if (e.button === 1) {
        dispatch(setIsMiddleMousePressed(true));
      }
    },
    [dispatch],
  );
};

export const useOnMouseUp = () => {
  const dispatch = useDispatch();

  return useCallback(
    (e: MouseEvent) => {
      if (e.button === 1) {
        dispatch(setIsMiddleMousePressed(false));
      }
    },
    [dispatch],
  );
};
